import { lazy, ComponentType } from "react";
import { isRecoverableModuleLoadError } from "@/lib/moduleLoadErrors";

const RETRY_KEY = "chunk-load-retry";

/**
 * Wraps React.lazy() with automatic retry logic for failed chunk loads.
 *
 * When a chunk fails to load (e.g., after a deployment with new hashes),
 * this will trigger a single page reload to fetch the new index.html
 * with correct chunk references.
 *
 * Uses sessionStorage to prevent infinite reload loops.
 */
export function lazyRetry<T extends ComponentType<unknown>>(
  componentImport: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const hasRetried = sessionStorage.getItem(RETRY_KEY) === "true";

    try {
      const component = await componentImport();
      // Success - clear the retry flag
      sessionStorage.removeItem(RETRY_KEY);
      return component;
    } catch (error) {
      // Check if this is a chunk/module load error
      const isChunkError = isRecoverableModuleLoadError(error);

      if (isChunkError && !hasRetried) {
        // First failure - set flag and reload to get fresh index.html
        sessionStorage.setItem(RETRY_KEY, "true");
        window.location.reload();
        // Return a never-resolving promise to prevent React from rendering
        // while the page reloads
        return new Promise(() => {});
      }

      // Either not a chunk error, or we already retried - let it propagate
      // to the error boundary
      sessionStorage.removeItem(RETRY_KEY);
      throw error;
    }
  });
}
